import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { IconButton } from './ui';

/**
 * AddJobModal – used for both time entry and expense entry job/task selection.
 *
 * Props:
 *   customers     – array of customer records
 *   onAdd         – callback(jobId, jobTaskId)
 *   onClose       – callback to close modal
 *   mode          – 'time' (default) or 'expense'
 *                   'time': excludes TaskCode=Expense, only shows T&M/Lump Sum tasks
 *                   'expense': only shows TaskCode=Expense tasks
 */
export default function AddJobModal({ customers, onAdd, onClose, mode = 'time', existingFavorites = [], multiSelect = false }) {
  const [freeform, setFreeform] = useState(false);

  // Multi-select task ids (favorites mode) – lets a user add several task lines
  // from the same job at once. In single-select mode this is unused.
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const toggleTaskId = (id) =>
    setSelectedTaskIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Dropdown mode state
  const [customerId, setCustomerId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState('');
  const [tasks, setTasks] = useState([]);
  const [jobTaskId, setJobTaskId] = useState('');
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Freeform mode state
  const [allJobs, setAllJobs] = useState([]);
  const [loadingAllJobs, setLoadingAllJobs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFreeformJob, setSelectedFreeformJob] = useState(null);
  const [freeformTasks, setFreeformTasks] = useState([]);
  const [freeformTaskId, setFreeformTaskId] = useState('');
  const [loadingFreeformTasks, setLoadingFreeformTasks] = useState(false);

  // Whether the job has zero open task lines of the applicable type (time or
  // expense) — i.e. the raw task list from NAV was empty, before removing tasks
  // already on the timesheet. Tracked per sub-mode (dropdown / freeform).
  const [dropRawEmpty, setDropRawEmpty] = useState(false);
  const [ffRawEmpty, setFfRawEmpty] = useState(false);

  // Build task filter options based on mode
  const taskFilterOpts = useMemo(() => {
    if (mode === 'expense') return { taskCode: 'Expense' };
    return { excludeTaskCode: 'Expense' };
  }, [mode]);

  // Load all jobs when freeform is enabled
  useEffect(() => {
    if (!freeform) return;
    if (allJobs.length > 0) return; // already loaded
    setLoadingAllJobs(true);
    api.getJobs()
      .then((j) => setAllJobs(j))
      .finally(() => setLoadingAllJobs(false));
  }, [freeform, allJobs.length]);

  // Dropdown: Load jobs when customer changes
  useEffect(() => {
    if (freeform) return;
    if (!customerId) {
      setJobs([]);
      setJobId('');
      return;
    }
    setLoadingJobs(true);
    api.getJobs(customerId)
      .then((j) => {
        setJobs(j);
        setJobId('');
        setTasks([]);
        setJobTaskId('');
      })
      .finally(() => setLoadingJobs(false));
  }, [customerId, freeform]);

  // Dropdown: Load tasks when job changes (with task code filter)
  useEffect(() => {
    if (freeform) return;
    if (!jobId) {
      setTasks([]);
      setJobTaskId('');
      setSelectedTaskIds([]);
      setDropRawEmpty(false);
      return;
    }
    setLoadingTasks(true);
    setDropRawEmpty(false);
    api.getJobTasks(jobId, taskFilterOpts)
      .then((t) => {
        setDropRawEmpty(t.length === 0);
        // Filter out tasks already on the timesheet
        const filtered = t.filter(
          (task) => !existingFavorites.some((f) => f.JobID === jobId && f.JobTaskID === task.JobTaskID)
        );
        setTasks(filtered);
        // Auto-select if only one task
        if (filtered.length === 1) {
          setJobTaskId(filtered[0].JobTaskID);
          setSelectedTaskIds([filtered[0].JobTaskID]);
        } else {
          setJobTaskId('');
          setSelectedTaskIds([]);
        }
      })
      .finally(() => setLoadingTasks(false));
  }, [jobId, freeform, taskFilterOpts, existingFavorites]);

  // Freeform: Load tasks when a job is selected (with task code filter)
  useEffect(() => {
    if (!freeform || !selectedFreeformJob) return;
    setLoadingFreeformTasks(true);
    setFreeformTaskId('');
    setSelectedTaskIds([]);
    setFfRawEmpty(false);
    api.getJobTasks(selectedFreeformJob.JobID, taskFilterOpts)
      .then((t) => {
        setFfRawEmpty(t.length === 0);
        // Filter out tasks already on the timesheet
        const filtered = t.filter(
          (task) => !existingFavorites.some((f) => f.JobID === selectedFreeformJob.JobID && f.JobTaskID === task.JobTaskID)
        );
        setFreeformTasks(filtered);
        // Auto-select if only one task
        if (filtered.length === 1) {
          setFreeformTaskId(filtered[0].JobTaskID);
          setSelectedTaskIds([filtered[0].JobTaskID]);
        }
      })
      .finally(() => setLoadingFreeformTasks(false));
  }, [freeform, selectedFreeformJob, taskFilterOpts, existingFavorites]);

  // Freeform: filtered jobs (searches job ID, job name, and customer name)
  const filteredJobs = useMemo(() => {
    if (!freeform) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return [];

    // Build a customer name lookup from the customers prop
    const custMap = {};
    for (const c of customers) {
      custMap[c.CustomerID] = c.CustomerName?.toLowerCase() || '';
    }

    return allJobs.filter((j) => {
      const custName = custMap[j.CustomerID] || '';
      const combined = `${j.JobID} ${j.JobName} ${custName}`.toLowerCase();
      return combined.includes(q);
    }).slice(0, 30);
  }, [freeform, allJobs, searchQuery, customers]);

  const activeJobId = freeform ? (selectedFreeformJob && selectedFreeformJob.JobID) : jobId;

  const handleAdd = () => {
    if (multiSelect) {
      if (activeJobId && selectedTaskIds.length > 0) {
        onAdd(activeJobId, selectedTaskIds);
      }
      return;
    }
    if (freeform) {
      if (selectedFreeformJob && freeformTaskId) {
        onAdd(selectedFreeformJob.JobID, freeformTaskId);
      }
    } else {
      if (jobId && jobTaskId) {
        onAdd(jobId, jobTaskId);
      }
    }
  };

  const canAdd = multiSelect
    ? !!(activeJobId && selectedTaskIds.length > 0)
    : freeform
      ? !!(selectedFreeformJob && freeformTaskId)
      : !!(jobId && jobTaskId);

  // Multi-select task list: checkboxes so several task lines from the same job
  // can be added to favorites at once.
  const renderTaskChecklist = (tasksArr, loading) => (
    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '6px' }}>
      {loading ? (
        <div style={{ padding: '8px 10px', fontSize: '13px', color: 'var(--gray-500)' }}>Loading...</div>
      ) : tasksArr.length === 0 ? (
        <div style={{ padding: '8px 10px', fontSize: '13px', color: 'var(--gray-500)' }}>No tasks available</div>
      ) : (
        tasksArr.map((t) => (
          <label key={t.JobTaskID} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}>
            <input type="checkbox" checked={selectedTaskIds.includes(t.JobTaskID)} onChange={() => toggleTaskId(t.JobTaskID)} />
            <span>{t.JobTaskID} &ndash; {t.TaskCode} &ndash; {t.TaskName}</span>
          </label>
        ))
      )}
    </div>
  );

  const modeWord = mode === 'expense' ? 'expense' : 'time';

  // Task line numbers from this job already on the user's timesheet (favorites),
  // sorted by task line number.
  const alreadyOnNumbers = (jobIdArg) => (existingFavorites || [])
    .filter((f) => String(f.JobID) === String(jobIdArg))
    .map((f) => f.JobTaskID)
    .sort((a, b) => {
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
      return String(a).localeCompare(String(b));
    });

  // Small red notes shown below the Job Task list.
  const renderTaskNotes = (jobIdArg, rawEmpty) => {
    if (!jobIdArg) return null;
    const nums = alreadyOnNumbers(jobIdArg);
    const noteStyle = { color: '#dc2626', fontSize: '11px', marginTop: '4px', lineHeight: 1.4 };
    return (
      <>
        {nums.length > 0 && (
          <div style={noteStyle}>
            {nums.length === 1
              ? `Job task line ${nums[0]} is already on timesheet`
              : `Job task lines ${nums.join(', ')} are already on timesheet`}
          </div>
        )}
        {rawEmpty && (
          <div style={noteStyle}>Job is open, but there are no open {modeWord} job task lines on the job.</div>
        )}
      </>
    );
  };

  const title = mode === 'expense' ? 'Select Job / Task for Expense' : 'Add Job / Task';

  // Render into document.body so the dialog (and its native <select> popups)
  // are never affected by a transformed or clipped ancestor.
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <IconButton icon="close" title="Close" onClick={onClose} />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={freeform}
              onChange={(e) => {
                setFreeform(e.target.checked);
                // Reset selections when toggling
                setSelectedFreeformJob(null);
                setFreeformTaskId('');
                setFreeformTasks([]);
                setSearchQuery('');
                setSelectedTaskIds([]);
                setJobTaskId('');
              }}
            />
            Freeform search
          </label>
        </div>

        {!freeform ? (
          <>
            <div className="form-group">
              <label>Customer</label>
              <select
                className="form-control"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">Select customer...</option>
                {customers.map((c) => (
                  <option key={c.CustomerID} value={c.CustomerID}>
                    {c.CustomerName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Job</label>
              <select
                className="form-control"
                value={jobId}
                onChange={(e) => setJobId(e.target.value)}
                disabled={!customerId || loadingJobs}
              >
                <option value="">{loadingJobs ? 'Loading...' : 'Select job...'}</option>
                {jobs.map((j) => (
                  <option key={j.JobID} value={j.JobID}>
                    {j.JobID} - {j.JobName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{multiSelect ? 'Job Tasks (select one or more)' : 'Job Task'}</label>
              {jobId && !loadingTasks && tasks.length === 0 && dropRawEmpty ? (
                <div className="form-control" style={{ background: 'var(--gray-50)', color: 'var(--gray-500)', fontSize: '13px' }}>
                  No task lines available
                </div>
              ) : jobId && !loadingTasks && tasks.length === 0 ? (
                <div className="form-control" style={{ background: 'var(--gray-50)', color: 'var(--gray-500)', fontSize: '13px' }}>
                  All tasks for this job are already on the timesheet
                </div>
              ) : multiSelect ? (
                renderTaskChecklist(tasks, loadingTasks)
              ) : tasks.length === 1 ? (
                <div className="form-control" style={{ background: 'var(--gray-50)' }}>
                  {tasks[0].JobTaskID} – {tasks[0].TaskCode} – {tasks[0].TaskName}
                </div>
              ) : (
                <select
                  className="form-control"
                  value={jobTaskId}
                  onChange={(e) => setJobTaskId(e.target.value)}
                  disabled={!jobId || loadingTasks}
                >
                  <option value="">{loadingTasks ? 'Loading...' : 'Select task...'}</option>
                  {tasks.map((t) => (
                    <option key={t.JobTaskID} value={t.JobTaskID}>
                      {t.JobTaskID} – {t.TaskCode} – {t.TaskName}
                    </option>
                  ))}
                </select>
              )}
              {renderTaskNotes(jobId, dropRawEmpty)}
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>Search by job, customer, or ID</label>
              <input
                className="form-control"
                type="text"
                placeholder={loadingAllJobs ? 'Loading jobs...' : 'Type to search...'}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedFreeformJob(null);
                  setFreeformTaskId('');
                  setFreeformTasks([]);
                }}
                disabled={loadingAllJobs}
              />
              {filteredJobs.length > 0 && !selectedFreeformJob && (
                <div style={{
                  maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--gray-200)',
                  borderRadius: '6px', marginTop: '4px', background: 'white',
                }}>
                  {filteredJobs.map((j) => {
                    const custName = customers.find((c) => c.CustomerID === j.CustomerID)?.CustomerName || '';
                    return (
                      <div
                        key={j.JobID}
                        onClick={() => {
                          setSelectedFreeformJob(j);
                          setSearchQuery(`${j.JobID} - ${j.JobName}`);
                        }}
                        style={{
                          padding: '6px 10px', cursor: 'pointer', fontSize: '13px',
                          borderBottom: '1px solid var(--gray-100)',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <div style={{ fontWeight: 500 }}>{j.JobID} - {j.JobName}</div>
                        {custName && <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{custName}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedFreeformJob && (
                <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>
                  Selected: {selectedFreeformJob.JobID} - {selectedFreeformJob.JobName}
                  <button
                    onClick={() => {
                      setSelectedFreeformJob(null);
                      setSearchQuery('');
                      setFreeformTaskId('');
                      setFreeformTasks([]);
                    }}
                    style={{ marginLeft: '8px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>{multiSelect ? 'Job Tasks (select one or more)' : 'Job Task'}</label>
              {selectedFreeformJob && !loadingFreeformTasks && freeformTasks.length === 0 && ffRawEmpty ? (
                <div className="form-control" style={{ background: 'var(--gray-50)', color: 'var(--gray-500)', fontSize: '13px' }}>
                  No task lines available
                </div>
              ) : selectedFreeformJob && !loadingFreeformTasks && freeformTasks.length === 0 ? (
                <div className="form-control" style={{ background: 'var(--gray-50)', color: 'var(--gray-500)', fontSize: '13px' }}>
                  All tasks for this job are already on the timesheet
                </div>
              ) : multiSelect ? (
                selectedFreeformJob ? renderTaskChecklist(freeformTasks, loadingFreeformTasks) : (
                  <div className="form-control" style={{ background: 'var(--gray-50)', color: 'var(--gray-500)', fontSize: '13px' }}>
                    Select a job first
                  </div>
                )
              ) : freeformTasks.length === 1 ? (
                <div className="form-control" style={{ background: 'var(--gray-50)' }}>
                  {freeformTasks[0].JobTaskID} – {freeformTasks[0].TaskCode} – {freeformTasks[0].TaskName}
                </div>
              ) : (
                <select
                  className="form-control"
                  value={freeformTaskId}
                  onChange={(e) => setFreeformTaskId(e.target.value)}
                  disabled={!selectedFreeformJob || loadingFreeformTasks}
                >
                  <option value="">{loadingFreeformTasks ? 'Loading...' : 'Select task...'}</option>
                  {freeformTasks.map((t) => (
                    <option key={t.JobTaskID} value={t.JobTaskID}>
                      {t.JobTaskID} – {t.TaskCode} – {t.TaskName}
                    </option>
                  ))}
                </select>
              )}
              {selectedFreeformJob && renderTaskNotes(selectedFreeformJob.JobID, ffRawEmpty)}
            </div>
          </>
        )}

        <button
          className="btn btn-primary btn-block"
          onClick={handleAdd}
          disabled={!canAdd}
        >
          {mode === 'expense'
            ? 'Select'
            : multiSelect && selectedTaskIds.length > 1
              ? `Add ${selectedTaskIds.length} to Favorites`
              : 'Add to Favorites'}
        </button>
      </div>
    </div>,
    document.body
  );
}
